package com.appsyde.shared.presentation.screen.screen

import androidx.compose.runtime.*
import com.appsyde.shared.app.presentation.account.AccountViewModel
import com.appsyde.shared.app.presentation.ticket.TicketState
import com.appsyde.shared.app.presentation.ticket.TicketTransitionState
import com.appsyde.shared.app.presentation.ticket.TicketViewModel
import com.appsyde.shared.modules.ai.domain.model.toneIndexToRefineAction
import com.appsyde.shared.modules.ai.domain.model.toneLabelOrNull
import com.appsyde.shared.modules.ticket.domain.model.Direction
import com.appsyde.shared.modules.ticket.domain.model.ExtractStatus
import com.appsyde.shared.presentation.screen.component.history.HistoryContentMode
import com.appsyde.shared.presentation.screen.component.history.HistorySection
import com.appsyde.shared.presentation.screen.component.message.MessageList
import com.appsyde.shared.presentation.screen.component.message.RespondCardHeader
import com.appsyde.shared.presentation.screen.component.message.TicketWorkflowStatus
import com.appsyde.shared.presentation.screen.component.message.TopMode
import com.appsyde.shared.presentation.screen.component.message.TopModeTabs
import com.appsyde.shared.presentation.screen.component.message.toTicketWorkflowStatus
import com.appsyde.shared.presentation.screen.component.menu.RightSidebar
import com.appsyde.shared.presentation.screen.component.menu.SideBarMenu
import com.appsyde.shared.presentation.screen.component.menu.SidebarDocument
import com.appsyde.shared.presentation.screen.component.message.helper.byKey
import com.appsyde.shared.presentation.screen.component.ticket.TicketsSidebar
import com.appsyde.shared.presentation.screen.screen.inbox.InboxEmptyView
import com.appsyde.shared.presentation.style.AppStylesTicketHeader
import com.appsyde.shared.presentation.style.animations.AppStylesAnimations
import com.appsyde.shared.presentation.style.style.*
import com.appsyde.shared.helper.ResolutionTitleComputer
import com.appsyde.contract.recommended.RecommendedActionComputer
import com.appsyde.contract.recommended.RecommendedActionState
import org.jetbrains.compose.web.ExperimentalComposeWebApi
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement

@OptIn(ExperimentalComposeWebApi::class)
@Composable
fun MainScreen(
    ticketViewModel: TicketViewModel,
    ticketsListViewModel: com.appsyde.shared.app.presentation.tickets.TicketsListViewModel,
    accountViewModel: AccountViewModel,
    logoutAction: () -> Unit
) {
    val state by ticketViewModel.ticketState.collectAsState()
    val accountState by accountViewModel.state.collectAsState()
    LaunchedEffect(Unit) { accountViewModel.loadAccount() }
    val toneRefineAction = toneIndexToRefineAction(accountState.toneIndex)
    val toneLabel = toneRefineAction.toneLabelOrNull() ?: "Ton"
    val sendState by ticketViewModel.sendState.collectAsState()
    val outboundAttachments by ticketViewModel.outboundAttachments.collectAsState()
    val attachmentErrors by ticketViewModel.attachmentErrors.collectAsState()
    val unreadCount = ticketViewModel.unreadCount.collectAsState()
    val processedToday = ticketViewModel.processedToday.collectAsState()
    val openCount = ticketViewModel.openCount.collectAsState()
    val waitingCount = ticketViewModel.waitingCount.collectAsState()
    val clarificationsSuggestedCount = ticketViewModel.clarificationsSuggestedCount.collectAsState()
    // Météo header (commenté pour l’instant — on verra plus tard si on l’affiche)
    // val weather = ticketViewModel.weather.collectAsState()
    // val weatherPercentDelta = ticketViewModel.weatherPercentDelta.collectAsState()

    val current = state as? TicketState.Success

    val selectedTicketId by ticketsListViewModel.selectedTicketId.collectAsState()

    val respond = current?.respond
    val ticket = respond?.ticket
    LaunchedEffect(ticket?.ticketId) {
        ticketViewModel.clearOutboundAttachments()
    }

    val selectedRespond by ticketsListViewModel.selectedRespond.collectAsState()
    val selectedExtract by ticketsListViewModel.selectedExtract.collectAsState()
    val ticketsListState by ticketsListViewModel.state.collectAsState()
    val currentUserAccountId = accountState.accountUserId

    // -------------------------
    // V2 sources
    // -------------------------
    val activeMessages = current?.activeMessages.orEmpty()
    val extract = current?.extract

    // -------------------------
    // Local UI editing state (but VM remains SoT)
    // -------------------------
    var isEditing by remember { mutableStateOf(false) }

    // Note: keyed by ticketId to reset local draft when ticket changes.
    // Use .value explicitly to avoid JS "n.y is not a function" with delegated mutableStateOf setter.
    val answerTextState = remember(ticket?.ticketId) {
        mutableStateOf(current?.replyText.orEmpty())
    }

    // Keep local answerText synced with VM unless user is editing
    LaunchedEffect(current?.replyText, isEditing) {
        val incoming = current?.replyText.orEmpty()
        if (!isEditing && incoming != answerTextState.value) {
            answerTextState.value = incoming
        }
    }

    // Styles
    Style(AppStylesAnimations)
    Style(AppStylesGlobal)
    Style(AppStylesHeader)
    Style(AppStylesLayout)
    Style(AppStylesCards)
    Style(AppStylesMessages)
    Style(AppStylesControls)
    Style(AppStylesHistory)
    Style(AppStylesRightSidebar)
    Style(AppStylesProgress)
    Style(AppStylesTicketHeader)
    Style(AppStylesAccount)
    Style(AppStylesWizard)
    Style(MainTopTabsStyles)

    val bodyScrollRef = remember { mutableStateOf<HTMLElement?>(null) }
    var topMode by remember { mutableStateOf(TopMode.TICKETS) }
    // Désélectionner le ticket quand on quitte la vue Tickets (pas à l’affichage, évite l’effet bizarre)
    var previousTopMode by remember { mutableStateOf(topMode) }
    val navigateToRespondId by ticketViewModel.navigateToRespondTicketId.collectAsState()
    LaunchedEffect(navigateToRespondId) {
        if (navigateToRespondId != null) {
            topMode = TopMode.RESPOND
            ticketsListViewModel.load()
            ticketViewModel.clearNavigateToRespond()
        }
    }
    val navigateToTickets by ticketViewModel.navigateToTicketsView.collectAsState()
    LaunchedEffect(navigateToTickets) {
        if (navigateToTickets) {
            topMode = TopMode.TICKETS
            ticketsListViewModel.load()
            ticketViewModel.clearNavigateToTicketsView()
        }
    }
    LaunchedEffect(topMode) {
        if (previousTopMode == TopMode.TICKETS && topMode != TopMode.TICKETS) {
            ticketsListViewModel.selectTicketById(null)
        }
        previousTopMode = topMode
    }

    Div(attrs = { classes(AppStylesGlobal.appRoot) }) {

        // -------------------------
        // Header
        // -------------------------
        Div(attrs = { classes(AppStylesHeader.header) }) {
            Div(attrs = { classes(AppStylesHeader.headerInner) }) {

                Div(attrs = { classes(AppStylesHeader.brandRow) }) {
                    Img("/appsydeIcon.png", attrs = {
                        classes(AppStylesHeader.brandIcon)
                        attr("alt", "AppSyde")
                    })
                    Div(attrs = { classes(AppStylesHeader.brandCol) }) {
                        Div(attrs = { classes(AppStylesHeader.brand) }) { Text("AppSyde") }
                        // Span(attrs = { classes(AppStylesHeader.brandSubtitle) }) { Text(weatherSubtitle) }
                    }
                }

                TopModeTabs(
                    mode = topMode,
                    onChange = { topMode = it }
                )

                Button(attrs = {
                    classes(AppStylesHeader.logoutButton)
                    if (topMode == TopMode.ACCOUNT) classes(MainTopTabsStyles.tabButtonActive)
                    attr("type", "button")
                    onClick { topMode = TopMode.ACCOUNT }
                }) { Text("Mon compte") }
            }
        }

        // -------------------------
        // Transition (Respond card)
        // -------------------------
        val transition = current?.transition ?: TicketTransitionState.Idle
        val transitionClass = when (transition) {
            TicketTransitionState.Exiting -> AppStylesCards.ticketCardExit
            TicketTransitionState.Entering -> AppStylesCards.ticketCardEnter
            TicketTransitionState.BackExiting -> AppStylesCards.ticketCardExitBack
            TicketTransitionState.BackEntering -> AppStylesCards.ticketCardEnterBack
            TicketTransitionState.Idle -> AppStylesCards.ticketCardIdle
        }

        Div(attrs = {
            classes(AppStylesGlobal.global, AppStylesLayout.globalContentWrap)
        }) {

            if (topMode == TopMode.ACCOUNT) {
                AccountScreen(onLogout = logoutAction)
            } else {
            Div(attrs = { classes(AppStylesLayout.container) }) {
                // -------------------------
                // Colonne 1 : SideBarMenu (Respond/Tickets)
                // -------------------------
                    val sidebarDocuments = remember(respond) {
                        respond?.messages
                            ?.flatMap { msg -> msg.attachments.map { att -> SidebarDocument(att, msg.messageId, msg.sentAtMs, msg.direction) } }
                            ?.sortedByDescending { it.sentAtMs }
                            ?: emptyList()
                    }
                    val sidebarClientName = remember(current?.sidebarDetected) {
                        val detected = current?.sidebarDetected.orEmpty()
                        detected.firstOrNull { it.key?.trim()?.lowercase() == "customer_name" }?.value?.trim()?.takeIf { it.isNotBlank() }
                            ?: detected.firstOrNull { it.key?.trim()?.lowercase() == "delivery_recipient" }?.value?.trim()?.takeIf { it.isNotBlank() }
                    }
                    SideBarMenu(
                        showClientInfoCard = ticket != null && topMode == TopMode.RESPOND,
                        detected1 = current?.sidebarDetected,
                        documents = sidebarDocuments,
                        ticketId = ticket?.ticketId,
                        clientEmail = ticket?.clientEmail?.trim()?.takeIf { it.isNotBlank() },
                        clientName = sidebarClientName,
                    )

                // -------------------------
                // Main column
                // -------------------------
                Div(attrs = { classes(AppStylesLayout.mainColumn) }) {

                    if (topMode == TopMode.RESPOND) {

                        // IMPORTANT:
                        // - The animated card element MUST stay stable across ticket changes (so exit animation is visible).
                        // - We key ONLY the inner content on ticketId.
                        Div(attrs = {
                            classes(
                                listOfNotNull(
                                    AppStylesCards.pageCardGlass,
                                    AppStylesCards.ticketCardAnimated,
                                    transitionClass
                                )
                            )
                        }) {

                            key(ticket?.ticketId ?: "no-ticket") {
                                if (ticket != null) {

                                    var historyMode by remember(ticket.ticketId) {
                                        mutableStateOf(HistoryContentMode.SUMMARY)
                                    }

                                    var workflowStatus by remember(ticket.ticketId) {
                                        mutableStateOf<TicketWorkflowStatus>(ticket.status.toTicketWorkflowStatus())
                                    }

                                    val hasHistory = ticket.historyMeta?.hasHistory == true
                                    val historyCount = ticket.historyMeta?.exchangesCount

                                    RespondCardHeader(
                                        subject = ticket.subject ?: "(Sans objet)",
                                        currentIntent = extract?.intent,
                                        detectedFields = extract?.detected,

                                        onBack = { ticketViewModel.goBack() },
                                        canGoBack = current.canGoBack,

                                        hasHistory = hasHistory,
                                        historyCount = historyCount,
                                        historyState = current.history,
                                        onToggleHistory = { ticketViewModel.toggleHistoryForCurrentTicket() },

                                        status = workflowStatus,
                                        onCycleStatus = {
                                            val newStatus = workflowStatus.next()
                                            ticketsListViewModel.updateTicketStatusLocally(
                                                ticketId = ticket.ticketId,
                                                newStatus = newStatus.name
                                            )
                                            ticketViewModel.updateStatus(newStatus.name)
                                            workflowStatus = newStatus
                                        },
                                        onSkip = {
                                            isEditing = false
                                            ticketViewModel.skip()
                                        }
                                    )

                                    Div(attrs = {
                                        classes(AppStylesCards.pageCardBody, AppStylesCards.pageCardBodyBg)
                                        ref { el ->
                                            bodyScrollRef.value = el
                                            onDispose { bodyScrollRef.value = null }
                                        }
                                    }) {

                                        HistorySection(
                                            history = current.history,
                                            originalHistory = current.originalHistory,
                                            hasHistory = hasHistory,
                                            contentMode = historyMode,
                                            onChangeMode = { mode ->
                                                historyMode = mode
                                                if (mode == HistoryContentMode.ORIGINAL) {
                                                    ticketViewModel.loadOriginalHistoryForCurrentTicket()
                                                } else {
                                                    ticketViewModel.loadHistoryForCurrentTicket()
                                                }
                                            },
                                            onCollapse = { ticketViewModel.toggleHistoryForCurrentTicket() }
                                        )

                                        MessageList(
                                            activeMessages = activeMessages,
                                            ticketId = ticket.ticketId,

                                            answerText = answerTextState.value,
                                            onAnswerChange = { v ->
                                                answerTextState.value = v
                                                // VM = source de vérité (persist local draft state)
                                                ticketViewModel.onReplyTextChanged(v)
                                            },
                                            onEditingChange = { editing ->
                                                isEditing = editing
                                                ticketViewModel.onEditingChanged(editing)
                                            },
                                            suggestedReplyDraft = current.suggestedReplyText,

                                            // Branchés sur les states VM
                                            isModified = current.isModified,
                                            isRegenerating = current.isRegenerating,

                                            // Actions VM
                                            regenerateAnswer = {
                                                isEditing = false
                                                ticketViewModel.regenerateAi()
                                            },
                                            onRefine = { text, action ->
                                                ticketViewModel.refineReply(text, action)
                                            },
                                            toneRefineAction = toneRefineAction,
                                            toneLabel = toneLabel,

                                            sendState = sendState,
                                            onSend = { ticketViewModel.sendReply(answerTextState.value) },
                                            outboundAttachments = outboundAttachments,
                                            attachmentErrors = attachmentErrors,
                                            onRemoveAttachment = { ticketViewModel.removeOutboundAttachment(it) },
                                            onAddAttachment = { ticketViewModel.addOutboundAttachment(it) },
                                            onSetAttachmentErrors = { ticketViewModel.setAttachmentErrors(it) },
                                            onClearAttachmentErrors = { ticketViewModel.setAttachmentErrors(emptyList()) },
                                            onClearAllAttachments = { ticketViewModel.clearOutboundAttachments() }
                                            )
                                    }

                                } else if (state is TicketState.Loading) {
                                    Div(attrs = { classes(AppStylesCards.pageCardBody, AppStylesCards.pageCardBodyBg) }) {
                                        Text("Chargement du ticket...")
                                    }
                                } else {
                                    InboxEmptyView(onTakeFirstTicket = { topMode = TopMode.TICKETS })
                                }

                            }
                        }

                    } else if (topMode == TopMode.TICKETS) {
                        TicketsScreenContent(ticketsListViewModel, currentUserAccountId)
                    }
                }

                // -------------------------
                // Right sidebar (card latérale séparée, comme TicketsSidebar)
                // -------------------------
                if (topMode == TopMode.RESPOND && ticket != null && current != null) {
                    val detectedMap = current.sidebarDetected.byKey()
                    val resolutionTitle = ResolutionTitleComputer.compute(
                        extract?.intent ?: ticket.currentIntent,
                        detectedMap
                    )

                    val recommendedState = RecommendedActionState(
                        intent = extract?.intent ?: ticket.currentIntent,
                        detected = detectedMap,
                        missing = current.missingKeys.map { it.trim().lowercase() }.filter { it.isNotBlank() }.toSet(),
                        toConfirm = current.toConfirmKeys.map { it.trim().lowercase() }.filter { it.isNotBlank() }.toSet()
                    )
                    val recommendedUi = RecommendedActionComputer.compute(recommendedState)

                    RightSidebar(
                        resolutionTitle = resolutionTitle,
                        recommendedAction = recommendedUi.labelFr,
                        recommendedActionWhy = recommendedUi.whyFr.orEmpty(),
                        missingInfo = current.missingKeys,
                        toConfirmKeys = current.toConfirmKeys,
                        isRefreshing = current.isRegenerating,
                        context = ticket.respondContext?.takeIf { it.isNotBlank() }
                            ?: extract?.respondContext,
                        needContext = true,
                        contextCollapseKey = ticket.ticketId,
                        onAddMemory = { key, value ->
                            ticketsListViewModel.removeMissingKeyLocally(ticket.ticketId, key)
                            ticketViewModel.addMemory(key, value)
                        },
                        onConfirmKey = { key ->
                            ticketsListViewModel.removeToConfirmKeyLocally(ticket.ticketId, key)
                            ticketViewModel.confirmKey(key)
                        },
                    )
                } else if (topMode == TopMode.TICKETS) {
                    val allTickets = ticketsListState.openTickets + ticketsListState.waitingTickets + ticketsListState.closedTickets
                    val extractTotal = allTickets.size
                    val extractAnalyzed = allTickets.count { it.extractStatus != ExtractStatus.PENDING && it.extractStatus != ExtractStatus.RUNNING }
                    val isOpeningForResponse by ticketViewModel.isOpeningForResponse.collectAsState()
                    TicketsSidebar(
                        respond = selectedRespond,
                        extract = selectedExtract,
                        extractAnalyzedCount = extractAnalyzed,
                        extractTotalCount = extractTotal,
                        currentUserAccountId = currentUserAccountId,
                        isOpeningForResponse = isOpeningForResponse,
                        onTraiterClick = {
                            selectedTicketId?.let { ticketViewModel.openTicketForResponse(it) }
                        },
                        onReprendreClick = selectedTicketId?.let { id ->
                            { ticketViewModel.takeoverTicket(id) }
                        },
                        onTousSesTicketsClick = { email ->
                            ticketsListViewModel.setSearchQuery(email)
                            topMode = TopMode.TICKETS
                        }
                    )
                }
            }
            }
        }
    }
}
